/**
 * Quick Access Component
 *
 * Provides quick access to favorites, recent items, and trash.
 * Part of the Data Workspace OS feature set.
 *
 * Features:
 * - Favorites section
 * - Recent items section
 * - Trash access
 */

import { Link, useRouterState } from '@tanstack/react-router';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
} from '@loader/ui';

import {
  FavoritesIcon,
  RecentIcon,
  TrashIcon,
} from '@/components/icons';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Quick access navigation component
 *
 * Provides shortcuts to:
 * - Favorites
 * - Recent items
 * - Trash
 */
export function QuickAccess() {
  const router = useRouterState();
  const currentPath = router.location.pathname;

  // Fetch tables and linked tables to calculate favorites count
  const { data: tables } = useTablesListQuery();
  const { data: linkedTables } = useLinkedTablesListQuery();

  // Calculate favorites count
  const favoritesCount =
    (tables?.filter((t) => t.is_favorite).length ?? 0) +
    (linkedTables?.filter((lt) => lt.is_favorite).length ?? 0);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Favorites"
              isActive={currentPath === '/workspace/favorites'}
            >
              <Link to="/workspace/favorites">
                <FavoritesIcon />
                <span>Favorites</span>
              </Link>
            </SidebarMenuButton>
            {favoritesCount > 0 && (
              <SidebarMenuBadge>{favoritesCount}</SidebarMenuBadge>
            )}
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Recent"
              isActive={currentPath === '/workspace/recent'}
            >
              <Link to="/workspace/recent">
                <RecentIcon />
                <span>Recent</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Trash"
              isActive={currentPath === '/workspace/trash'}
            >
              <Link to="/workspace/trash">
                <TrashIcon />
                <span>Trash</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
